/**
 * Hook for fetching data needed in Step 1 of project initialisation:
 * - Task template sets
 * - Scheduling projects (from Sitedrive)
 * - Users (for Tyopaallikko and Vastaava tyonjohtaja selection)
 */

import { gql, useQuery } from "@apollo/client";

const GET_TASK_TEMPLATE_SETS = gql`
  query GetTaskTemplateSets {
    taskTemplateSets(limit: 100) {
      items {
        id
        name
        description
      }
    }
  }
`;

const GET_SCHEDULING_PROJECTS = gql`
  query GetSchedulingProjects {
    schedulingProjects {
      id
      name
      projectCode
      state
      siteId
    }
  }
`;

const GET_USERS_FOR_INIT = gql`
  query GetUsersForInit($search: String) {
    users(search: $search, limit: 100) {
      items {
        id
        email
        firstName
        lastName
        isActive
        substituteUserId
        substituteFirstName
        substituteLastName
      }
    }
  }
`;

export interface TaskTemplateSetOption {
  id: string;
  name: string;
  description?: string | null;
}

export interface SchedulingProjectOption {
  id: string;
  name: string;
  projectCode?: string | null;
  state?: string | null;
  siteId?: string | null;
}

export interface UserOption {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  isActive: boolean;
  substituteUserId?: string | null;
  substituteFirstName?: string | null;
  substituteLastName?: string | null;
}

export function useProjectInitialisationData(userSearch?: string) {
  const { data: templateSetsData, loading: templateSetsLoading } = useQuery(
    GET_TASK_TEMPLATE_SETS,
    { fetchPolicy: "cache-and-network" }
  );

  const { data: schedulingProjectsData, loading: schedulingProjectsLoading } = useQuery(
    GET_SCHEDULING_PROJECTS,
    { fetchPolicy: "cache-and-network" }
  );

  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS_FOR_INIT, {
    variables: { search: userSearch || undefined },
    fetchPolicy: "cache-and-network",
  });

  return {
    templateSets: (templateSetsData?.taskTemplateSets?.items ?? []) as TaskTemplateSetOption[],
    schedulingProjects: (schedulingProjectsData?.schedulingProjects ?? []) as SchedulingProjectOption[],
    users: (usersData?.users?.items ?? []) as UserOption[],
    loading: templateSetsLoading || schedulingProjectsLoading || usersLoading,
  };
}
